import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, decomposeColor, styled, useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// ─── Requested (awaitingConfirmation) marking ─────────────────────────────────
// Diagonal hatch overlay used to flag the share of a slot's occupancy that is
// still only requested (status awaitingConfirmation) and not yet confirmed. The
// slot stays blocked, but staff can tell at a glance that it is not a firm booking.
export const REQUESTED_HATCH_IMAGE =
  'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.6) 3px, rgba(255,255,255,0.6) 6px)';

// Returns the requested-occupied units of a slot, clamped to [0, occupied].
const getRequestedOccupied = slot => {
  const requested = Number(slot?.requestedOccupied ?? 0);
  if (!Number.isFinite(requested) || requested <= 0) return 0;
  const occupied = Number(slot?.occupied ?? 0);
  return Number.isFinite(occupied) ? Math.min(requested, Math.max(0, occupied)) : requested;
};

// ─── Shared color logic ───────────────────────────────────────────────────────

const mixPaletteColors = (color1, color2, t) => {
  const a = decomposeColor(color1).values;
  const b = decomposeColor(color2).values;
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r}, ${g}, ${bl})`;
};

const getColor = (theme, percentage, useResources = true, occupied = 0) => {
  const useResourcesBool =
    typeof useResources === 'string' ? useResources === 'true' : useResources;

  const none = theme.palette.mode === 'light' ? theme.palette.grey[900] : theme.palette.grey[700];
  const low = theme.palette.success.main;

  if (!useResourcesBool) {
    return occupied === 0 ? none : theme.palette.error.main;
  }

  if (occupied === 0) return none;

  const pctRaw = typeof percentage === 'string' ? Number(percentage) : percentage;
  const pct = Number.isFinite(pctRaw) ? Math.max(0, Math.min(100, pctRaw)) : 0;

  if (pct === 0) return low;

  const w = theme.palette.warning.main;
  const full = '#d50000';

  if (pct <= 50) {
    return mixPaletteColors(low, w, pct / 50);
  }
  return mixPaletteColors(w, full, (pct - 50) / 50);
};

// ─── Shared styled components for table views ─────────────────────────────────

const StyledTableCell = styled(TableCell)(({ theme, percentage, occupied, useresources }) => {
  const bg = getColor(theme, percentage, useresources, occupied);
  return {
    backgroundColor: bg,
    color: theme.palette.common.white,
    textShadow: '0 1px 2px rgba(0,0,0,0.65)',
    textAlign: 'center',
    padding: '4px',
    fontSize: '0.875rem',
  };
});

const HourDisplay = ({ time }) => {
  const baseSx = { lineHeight: 1, display: 'block' };
  const [hourPart = '', minutePart = ''] = String(time || '').split(':');
  if (minutePart && minutePart !== '00') {
    return (
      <Typography component='div' variant='body2' sx={{ ...baseSx, fontSize: '0.75rem' }}>
        {minutePart}
      </Typography>
    );
  }
  return (
    <Typography component='div' variant='body1' sx={{ ...baseSx, fontWeight: 'bold' }}>
      {hourPart}
    </Typography>
  );
};

const parseTimeToMinutes = time => {
  const [hour, minute] = String(time || '').split(':').map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 60 + minute;
};

const isCurrentDay = value => {
  const parsed = dayjs(value);
  return parsed.isValid() && parsed.isSame(dayjs(), 'day');
};

// ─── Condensed table view (condensedMode = true, from original) ───────────────

/**
 * Detects the interval in minutes between consecutive time slots.
 * Returns 30 if it cannot be determined.
 */
const detectSlotIntervalMinutes = times => {
  if (!Array.isArray(times) || times.length < 2) return 30;
  const parse = t => {
    const [h, m] = String(t?.time || '').split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
  };
  const m0 = parse(times[0]);
  const m1 = parse(times[1]);
  if (m0 == null || m1 == null || m1 <= m0) return 30;
  return m1 - m0;
};

/**
 * Aggregates sub-30-minute slots into 30-minute buckets for the condensed view.
 * Each bucket takes the label of its first slot, the maximum percentageOccupied,
 * and sums occupied / free counts.
 */
const aggregateTo30MinSlots = times => {
  const intervalMinutes = detectSlotIntervalMinutes(times);
  if (intervalMinutes >= 30) return times;

  const parse = t => {
    const [h, m] = String(t?.time || '').split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
  };

  const buckets = new Map();
  for (const slot of times) {
    const totalMin = parse(slot);
    if (totalMin == null) continue;
    // Round down to nearest 30-minute boundary
    const bucketMin = Math.floor(totalMin / 30) * 30;
    if (!buckets.has(bucketMin)) {
      buckets.set(bucketMin, {
        slot,
        occupied: 0,
        requestedOccupied: 0,
        free: null,
        pct: 0,
        absoluteCount: 0,
      });
    }
    const b = buckets.get(bucketMin);
    const occ = Number(slot.occupied ?? 0) || 0;
    const pct = Number(slot.percentageOccupied ?? 0) || 0;
    b.occupied += occ;
    b.requestedOccupied += getRequestedOccupied(slot);
    // Take the maximum percentage within this 30-min window
    if (pct > b.pct) b.pct = pct;
    if (slot.free != null) {
      b.free = (b.free ?? 0) + Number(slot.free);
    }
    b.absoluteCount += Number(slot.absoluteCount ?? slot.occupied ?? 0) || 0;
  }

  return Array.from(buckets.values()).map(
    ({ slot, occupied, requestedOccupied, free, pct, absoluteCount }) => ({
      ...slot,
      occupied,
      requestedOccupied,
      free,
      percentageOccupied: pct,
      absoluteCount,
    })
  );
};

const CondensedTableView = ({
  data,
  showAbsoluteCountInHeader,
  showTotalColumn,
  onToggleExpand,
  currentDate,
  autoScrollToCurrentTime,
  highlightCurrentSlot,
}) => {
  const theme = useTheme();
  const isRealSlot = slot => !(slot && slot.isSynthetic === true);
  const tableContainerRef = useRef(null);
  const slotCellRefs = useRef(new Map());
  const [nowTs, setNowTs] = useState(() => Date.now());

  // Aggregate sub-30-min intervals into 30-min buckets so colours align with the booking grid
  const displayTimes = useMemo(() => aggregateTo30MinSlots(data?.times || []), [data?.times]);
  const shouldTrackCurrentTime =
    (autoScrollToCurrentTime || highlightCurrentSlot) && isCurrentDay(currentDate);

  const totalAbsoluteCount = displayTimes.reduce(
    (acc, slot) => acc + (isRealSlot(slot) ? (Number(slot?.absoluteCount ?? slot?.occupied ?? 0) || 0) : 0),
    0
  );
  const totalOccupied = displayTimes.reduce(
    (acc, slot) => acc + (isRealSlot(slot) ? (Number(slot?.occupied) || 0) : 0),
    0
  );
  const totalFree = displayTimes.reduce(
    (acc, slot) => acc + (isRealSlot(slot) ? (Number(slot?.free) || 0) : 0),
    0
  );

  useEffect(() => {
    if (!shouldTrackCurrentTime) return undefined;

    const intervalId = window.setInterval(() => {
      setNowTs(Date.now());
    }, 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [shouldTrackCurrentTime]);

  const currentSlotTime = useMemo(() => {
    if (!shouldTrackCurrentTime || !displayTimes.length) return null;

    const now = new Date(nowTs);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    let closestPastOrCurrent = null;

    for (const slot of displayTimes) {
      const slotMinutes = parseTimeToMinutes(slot?.time);
      if (slotMinutes == null) continue;
      if (slotMinutes <= nowMinutes) {
        closestPastOrCurrent = slot?.time || null;
        continue;
      }
      break;
    }

    if (closestPastOrCurrent) return closestPastOrCurrent;
    return displayTimes.find(slot => parseTimeToMinutes(slot?.time) != null)?.time || null;
  }, [displayTimes, nowTs, shouldTrackCurrentTime]);

  useEffect(() => {
    if (!autoScrollToCurrentTime || !currentSlotTime) return;

    const container = tableContainerRef.current;
    const slotCell = slotCellRefs.current.get(currentSlotTime);
    if (!container || !slotCell) return;

    const leftPadding = 24;
    const rightPadding = 24;
    const visibleLeft = container.scrollLeft + leftPadding;
    const visibleRight = container.scrollLeft + container.clientWidth - rightPadding;
    const slotLeft = slotCell.offsetLeft;
    const slotRight = slotLeft + slotCell.offsetWidth;

    if (slotLeft >= visibleLeft && slotRight <= visibleRight) return;

    const targetLeft = Math.max(0, slotLeft - container.clientWidth / 2 + slotCell.offsetWidth / 2);
    container.scrollTo({ left: targetLeft, behavior: 'smooth' });
  }, [autoScrollToCurrentTime, currentSlotTime]);

  return (
    <TableContainer
      ref={tableContainerRef}
      component={Paper}
      sx={{
        position: 'relative',
        scrollBehavior: 'smooth',
        '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
        '&::-webkit-scrollbar-track': { display: 'none' },
        '&::-webkit-scrollbar-thumb': { display: 'none' },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '-ms-overflow-style': 'none',
      }}
    >
      {(data.entertainmentName || data.capacity != null || onToggleExpand) && (
        <Box
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            pt: 0.75,
            pb: 0.25,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            {data.entertainmentName && (
              <Typography variant='subtitle2' sx={{ fontWeight: 700, lineHeight: 1 }}>
                {data.entertainmentName}
              </Typography>
            )}
            {data.capacity != null && (
              <Typography variant='caption' sx={{ opacity: 0.6, lineHeight: 1 }}>
                Slotkapazität: {data.capacity}
              </Typography>
            )}
            {data.totalBookings != null && (
              <Typography variant='caption' sx={{ opacity: 0.6, lineHeight: 1 }}>
                · insgesamt {data.totalBookings} Buchung{data.totalBookings !== 1 ? 'en' : ''}
              </Typography>
            )}
            {data.ageSplit && (
              <Typography variant='caption' sx={{ opacity: 0.55, lineHeight: 1 }}>
                · Kinder-Kapazität wird schraffiert dargestellt
              </Typography>
            )}
          </Box>
          {onToggleExpand && (
            <Tooltip title='Detailansicht' arrow placement='left'>
              <IconButton
                size='small'
                onClick={onToggleExpand}
                sx={{
                  ml: 1,
                  padding: '2px',
                  flexShrink: 0,
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                }}
              >
                <ExpandMoreIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      <Table sx={{ width: '100%', tableLayout: 'fixed', minWidth: `${Math.max(850, displayTimes.length * 22)}px` }} aria-label='attendance table'>
        <TableHead>
          <TableRow>
            {displayTimes.map((slot, index) => {
              const hasFree = data.usesResources && slot.free != null;
              const freeLabel = hasFree ? ` / F: ${slot.free}` : '';
              const isCurrentSlot = highlightCurrentSlot && currentSlotTime === slot.time;
              const isAgeSplitSlot = data.ageSplit && slot.freesKids != null;
              const requestedOcc = getRequestedOccupied(slot);
              const showRequested = requestedOcc > 0 && !isAgeSplitSlot;

              let ageSplitCellSx = {};
              let tooltipTitle;

              if (isAgeSplitSlot) {
                const sgt = slot.slotGroupType;
                const kidsOcc = Number(slot.kidsCount || 0);
                const adultsOcc = Number(slot.adultsCount || 0);
                const kidsTotal = slot.totalKidsCapacity != null
                  ? Number(slot.totalKidsCapacity)
                  : kidsOcc + Math.max(0, Number(slot.freesKids || 0));
                const adultsTotal = slot.totalAdultsCapacity != null
                  ? Number(slot.totalAdultsCapacity)
                  : adultsOcc + Math.max(0, Number(slot.freesAdults || 0));
                const kidsPct = kidsTotal > 0 ? Math.min(100, Math.round((kidsOcc / kidsTotal) * 100)) : 0;
                const adultsPct = adultsTotal > 0 ? Math.min(100, Math.round((adultsOcc / adultsTotal) * 100)) : 0;
                const kidsColor = getColor(theme, kidsPct, true, kidsOcc);
                const adultsColor = getColor(theme, adultsPct, true, adultsOcc);

                if (sgt === 'adults') {
                  ageSplitCellSx = { backgroundColor: adultsColor };
                  tooltipTitle = `E: ${adultsOcc} belegt / ${Math.max(0, Number(slot.freesAdults))} frei / ${adultsPct}%`;
                } else if (sgt === 'kids') {
                  ageSplitCellSx = {
                    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.25) 2px, rgba(0,0,0,0.25) 4px)`,
                    backgroundAttachment: 'fixed',
                    backgroundColor: kidsColor,
                  };
                  tooltipTitle = `K: ${kidsOcc} belegt / ${Math.max(0, Number(slot.freesKids))} frei / ${kidsPct}%`;
                } else {
                  ageSplitCellSx = {
                    background: `linear-gradient(to bottom, ${kidsColor} 50%, ${adultsColor} 50%)`,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.25) 2px, rgba(0,0,0,0.25) 4px)',
                      backgroundAttachment: 'fixed',
                      pointerEvents: 'none',
                    },
                  };
                  tooltipTitle = `K: ${kidsOcc} belegt / ${Math.max(0, Number(slot.freesKids))} frei / ${kidsPct}% · E: ${adultsOcc} belegt / ${Math.max(0, Number(slot.freesAdults))} frei / ${adultsPct}%`;
                }
              } else {
                tooltipTitle = showAbsoluteCountInHeader
                  ? `P: ${Math.round(Number(slot.absoluteCount ?? slot.occupied ?? 0) || 0)} / B: ${slot.occupied}${freeLabel} / ${slot.percentageOccupied}% Auslastung`
                  : `B: ${slot.occupied}${freeLabel} / ${slot.percentageOccupied}% Auslastung`;
                if (showRequested) {
                  tooltipTitle += ` · davon ${requestedOcc} angefragt (noch nicht bestätigt)`;
                }
              }

              return (
              <Tooltip
                key={index}
                title={tooltipTitle}
                arrow
                disableHoverListener={!data.usesResources && !isAgeSplitSlot}
              >
                <StyledTableCell
                  ref={element => {
                    if (!element) {
                      slotCellRefs.current.delete(slot.time);
                      return;
                    }
                    slotCellRefs.current.set(slot.time, element);
                  }}
                  percentage={isAgeSplitSlot ? 0 : slot.percentageOccupied}
                  useresources={data.usesResources.toString()}
                  occupied={isAgeSplitSlot ? 0 : slot.occupied}
                  sx={theme => ({
                    borderBottom: data.usesResources ? 'none' : '',
                    position: 'relative',
                    ...(isAgeSplitSlot ? ageSplitCellSx : {}),
                    ...(isCurrentSlot
                      ? {
                          outline: `2px solid ${alpha(theme.palette.info.main, 0.95)}`,
                          outlineOffset: '-2px',
                          boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.info.main, 0.65)}`,
                          zIndex: 1,
                        }
                      : {}),
                  })}
                >
                  {showRequested ? (
                    <Box
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        backgroundImage: REQUESTED_HATCH_IMAGE,
                        zIndex: 0,
                      }}
                    />
                  ) : null}
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      display: 'grid',
                      gridTemplateRows: showAbsoluteCountInHeader ? '18px auto' : '18px',
                      justifyItems: 'center',
                      alignItems: 'end',
                      rowGap: showAbsoluteCountInHeader ? 0.25 : 0,
                      lineHeight: 1,
                    }}
                  >
                    <Box sx={{ height: 18, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                      <HourDisplay time={slot.time} />
                    </Box>
                    {showAbsoluteCountInHeader ? (
                      <Typography sx={{ fontSize: '0.7rem', opacity: 0.95, lineHeight: 1 }}>
                        {Math.round(Number(slot.absoluteCount ?? slot.occupied ?? 0) || 0)}
                      </Typography>
                    ) : null}
                  </Box>
                </StyledTableCell>
              </Tooltip>
              );
            })}
            {showTotalColumn ? (
              <StyledTableCell
                percentage={0}
                useresources={data.usesResources.toString()}
                occupied={0}
                sx={theme => ({
                  backgroundColor:
                    theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[800],
                  borderLeft: `1px solid ${theme.palette.divider}`,
                  color: theme.palette.getContrastText(
                    theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[800]
                  ),
                })}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateRows: showAbsoluteCountInHeader ? '18px auto' : '18px',
                    justifyItems: 'center',
                    alignItems: 'end',
                    rowGap: showAbsoluteCountInHeader ? 0.25 : 0,
                    lineHeight: 1,
                  }}
                >
                  <Box sx={{ height: 18, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                    <Typography component='div' variant='body1' sx={{ lineHeight: 1, display: 'block', fontWeight: 'bold' }}>
                      Σ
                    </Typography>
                  </Box>
                  {showAbsoluteCountInHeader ? (
                    <Typography sx={{ fontSize: '0.7rem', opacity: 0.95, lineHeight: 1 }}>
                      {Math.round(totalAbsoluteCount)}
                    </Typography>
                  ) : null}
                </Box>
              </StyledTableCell>
            ) : null}
          </TableRow>
        </TableHead>

        {!data.usesResources && (
          <TableBody>
            <TableRow>
              {displayTimes.map((slot, index) => {
                const isCurrentSlot = highlightCurrentSlot && currentSlotTime === slot.time;
                return (
                <StyledTableCell
                  key={index}
                  percentage={slot.percentageOccupied}
                  useresources={data.usesResources.toString()}
                  occupied={slot.occupied}
                  sx={
                    isCurrentSlot
                      ? theme => ({
                          outline: `2px solid ${alpha(theme.palette.info.main, 0.95)}`,
                          outlineOffset: '-2px',
                          boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.info.main, 0.65)}`,
                        })
                      : undefined
                  }
                >
                  {slot.occupied}
                </StyledTableCell>
                );
              })}
              {showTotalColumn ? (
                <StyledTableCell
                  percentage={0}
                  useresources={data.usesResources.toString()}
                  occupied={0}
                  sx={theme => ({
                    backgroundColor:
                      theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[800],
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    color: theme.palette.getContrastText(
                      theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[800]
                    ),
                  })}
                >
                  {Math.round(totalOccupied)}
                </StyledTableCell>
              ) : null}
            </TableRow>
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

// ─── Bar chart view (condensedMode = false) ───────────────────────────────────

// Chart height in grid-aligned mode (inside the grid scroll container)
const CHART_HEIGHT = 72;

/**
 * Bar chart view.
 *
 * When `slotWidthPx` and `startOffsetPx` are supplied the component is rendered
 * in "grid mode": it sits inside the same horizontally-scrollable container as
 * the booking grid so the bars are pixel-perfectly aligned with the time slots.
 * The outer Paper/scroll wrapper is then omitted – the caller is responsible for
 * placing this component at the correct position.
 *
 * Without those props it falls back to the standalone scrollable variant used
 * in the Resources view.
 */
const BarChartView = ({
  data,
  showAbsoluteCountInHeader,
  onToggleExpand,
  slotWidthPx,
  startOffsetPx = 0,
}) => {
  const theme = useTheme();
  const isRealSlot = slot => !(slot && slot.isSynthetic === true);
  const isGridMode = slotWidthPx != null && slotWidthPx > 0;

  // When no capacity is configured (usesResources = false), assume a default of 10
  // so bars are scaled against that reference and the colour gradient behaves normally.
  const DEFAULT_CAPACITY = 10;
  const effectiveCapacity = data.usesResources
    ? Number(data.capacity) || DEFAULT_CAPACITY
    : DEFAULT_CAPACITY;

  const bars = data.times.map((slot, index) => {
    const [, minutePart = ''] = String(slot.time || '').split(':');
    const isFullHour = minutePart === '00';

    const colWidth = isGridMode ? slotWidthPx : undefined;
    const colSx = isGridMode
      ? { width: colWidth, minWidth: colWidth, maxWidth: colWidth, flexShrink: 0, px: '1px' }
      : { flex: '1 1 0', minWidth: 22, px: '1px' };

    const timeLabel = (
      <Box
        sx={{
          textAlign: 'center',
          py: '2px',
          height: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderTop: theme => `1px solid ${theme.palette.divider}`,
          backgroundColor: theme =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[200]
              : alpha(theme.palette.common.black, 0.35),
          userSelect: 'none',
        }}
      >
        <Typography
          component='div'
          sx={{
            fontSize: isFullHour ? '0.8rem' : '0.65rem',
            fontWeight: isFullHour ? 700 : 400,
            lineHeight: 1,
            color: isFullHour ? 'text.primary' : 'text.secondary',
          }}
        >
          {isFullHour ? String(slot.time).split(':')[0] : minutePart}
        </Typography>
      </Box>
    );

    if (data.ageSplit && slot.freesKids != null) {
      const sgt = slot.slotGroupType;
      const kidsOcc = Number(slot.kidsCount || 0);
      const adultsOcc = Number(slot.adultsCount || 0);
      const kidsTotal = slot.totalKidsCapacity != null
        ? Number(slot.totalKidsCapacity)
        : kidsOcc + Math.max(0, Number(slot.freesKids || 0));
      const adultsTotal = slot.totalAdultsCapacity != null
        ? Number(slot.totalAdultsCapacity)
        : adultsOcc + Math.max(0, Number(slot.freesAdults || 0));
      const kidsPct = kidsTotal > 0 ? Math.min(100, Math.round((kidsOcc / kidsTotal) * 100)) : 0;
      const adultsPct = adultsTotal > 0 ? Math.min(100, Math.round((adultsOcc / adultsTotal) * 100)) : 0;
      const kidsBarHeight = Math.round((kidsPct / 100) * CHART_HEIGHT);
      const adultsBarHeight = Math.round((adultsPct / 100) * CHART_HEIGHT);
      const kidsColor = getColor(theme, kidsPct, true, kidsOcc);
      const adultsColor = getColor(theme, adultsPct, true, adultsOcc);

      const renderHalfBar = (label, occ, freeCount, pct, barHeight, barColor, hatched, borderRadiusLeft) => (
        <Tooltip
          title={`${label === 'K' ? 'Kinder' : 'Erwachsene'}: ${occ} belegt / ${Math.max(0, freeCount)} frei / ${pct}%`}
          arrow
          placement='top'
        >
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', cursor: 'default', minWidth: 0 }}>
            <Box
              sx={{
                width: '100%',
                height: CHART_HEIGHT,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                backgroundColor: theme =>
                  theme.palette.mode === 'light'
                    ? theme.palette.grey[200]
                    : alpha(theme.palette.common.black, 0.25),
                borderRadius: borderRadiusLeft ? '3px 0 0 0' : '0 3px 0 0',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {[25, 50, 75].map(mark => (
                <Box
                  key={mark}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: `${mark}%`,
                    height: '1px',
                    backgroundColor: theme =>
                      theme.palette.mode === 'light'
                        ? alpha(theme.palette.common.black, 0.08)
                        : alpha(theme.palette.common.white, 0.08),
                    pointerEvents: 'none',
                  }}
                />
              ))}
              <Box
                sx={{
                  width: '100%',
                  height: `${barHeight}px`,
                  backgroundColor: barColor,
                  transition: 'height 0.3s ease, background-color 0.3s ease',
                  borderRadius: barHeight > 0
                    ? borderRadiusLeft ? '2px 0 0 0' : '0 2px 0 0'
                    : 0,
                  ...(hatched ? {
                    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,0.25) 3px, rgba(0,0,0,0.25) 6px)',
                  } : {}),
                }}
              />
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: 1,
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  color: theme =>
                    theme.palette.mode === 'light'
                      ? alpha(theme.palette.common.black, 0.65)
                      : alpha(theme.palette.common.white, 0.92),
                  lineHeight: 1,
                  zIndex: 1,
                  textShadow: theme =>
                    theme.palette.mode === 'light'
                      ? '0 0 2px rgba(255,255,255,0.75)'
                      : '0 0 2px rgba(0,0,0,0.8)',
                }}
              >
                {label}
              </Typography>
            </Box>
          </Box>
        </Tooltip>
      );

      if (sgt === 'adults') {
        return (
          <Box key={index} sx={{ ...colSx, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
              {renderHalfBar('E', adultsOcc, slot.freesAdults, adultsPct, adultsBarHeight, adultsColor, false, true)}
            </Box>
            {timeLabel}
          </Box>
        );
      }
      if (sgt === 'kids') {
        return (
          <Box key={index} sx={{ ...colSx, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
              {renderHalfBar('K', kidsOcc, slot.freesKids, kidsPct, kidsBarHeight, kidsColor, true, true)}
            </Box>
            {timeLabel}
          </Box>
        );
      }

      return (
        <Box key={index} sx={{ ...colSx, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '1px', width: '100%' }}>
            {renderHalfBar('K', kidsOcc, slot.freesKids, kidsPct, kidsBarHeight, kidsColor, true, true)}
            {renderHalfBar('E', adultsOcc, slot.freesAdults, adultsPct, adultsBarHeight, adultsColor, false, false)}
          </Box>
          {timeLabel}
        </Box>
      );
    }

    const occ = Number(slot.occupied ?? 0);
    const hasFree = data.usesResources && slot.free != null;
    const free = hasFree ? Number(slot.free) : null;
    const pct = data.usesResources
      ? Number(slot.percentageOccupied) || Math.round((occ / effectiveCapacity) * 100)
      : Math.round((occ / effectiveCapacity) * 100);
    const barColor = getColor(theme, pct, true, occ);
    const barHeightPx = Math.round((pct / 100) * CHART_HEIGHT);

    // Top slice of the filled bar that represents still-requested (unconfirmed) units.
    const requestedOcc = getRequestedOccupied(slot);
    const requestedHeightPx =
      requestedOcc > 0 && barHeightPx > 0 && occ > 0
        ? Math.min(barHeightPx, Math.max(2, Math.round(barHeightPx * (requestedOcc / occ))))
        : 0;

    const freeLabel = hasFree ? ` / F: ${free}` : '';
    let tooltipContent = showAbsoluteCountInHeader
      ? `P: ${Math.round(Number(slot.absoluteCount ?? slot.occupied ?? 0) || 0)} / B: ${occ}${freeLabel} / ${pct}% Auslastung`
      : `B: ${occ}${freeLabel} / ${pct}% Auslastung`;
    if (requestedOcc > 0) {
      tooltipContent += ` · davon ${requestedOcc} angefragt (noch nicht bestätigt)`;
    }

    return (
      <Tooltip key={index} title={tooltipContent} arrow placement='top'>
        <Box sx={{ ...colSx, display: 'flex', flexDirection: 'column', cursor: 'default' }}>
          {showAbsoluteCountInHeader && isRealSlot(slot) ? (
            <Typography
              sx={{ fontSize: '0.6rem', opacity: 0.75, lineHeight: 1, mb: '1px', userSelect: 'none', textAlign: 'center' }}
            >
              {Math.round(Number(slot.absoluteCount ?? slot.occupied ?? 0) || 0)}
            </Typography>
          ) : null}

          <Box
            sx={{
              width: '100%',
              height: CHART_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              backgroundColor:
                theme.palette.mode === 'light'
                  ? theme.palette.grey[200]
                  : alpha(theme.palette.common.black, 0.4),
              borderRadius: '3px 3px 0 0',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {[25, 50, 75].map(mark => (
              <Box
                key={mark}
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: `${mark}%`,
                  height: '1px',
                  backgroundColor:
                    theme.palette.mode === 'light'
                      ? alpha(theme.palette.common.black, 0.08)
                      : alpha(theme.palette.common.white, 0.08),
                  pointerEvents: 'none',
                }}
              />
            ))}
            <Box
              sx={{
                width: '100%',
                height: `${barHeightPx}px`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'height 0.3s ease, background-color 0.3s ease',
                borderRadius: barHeightPx > 0 ? '2px 2px 0 0' : 0,
              }}
            >
              {requestedHeightPx > 0 ? (
                <Box
                  sx={{
                    width: '100%',
                    height: `${requestedHeightPx}px`,
                    backgroundColor: barColor,
                    backgroundImage: REQUESTED_HATCH_IMAGE,
                  }}
                />
              ) : null}
              <Box sx={{ flex: 1, width: '100%', backgroundColor: barColor }} />
            </Box>
          </Box>

          <Box
            sx={{
              textAlign: 'center',
              py: '2px',
              height: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              backgroundColor:
                theme.palette.mode === 'light'
                  ? theme.palette.grey[100]
                  : alpha(theme.palette.common.black, 0.5),
              userSelect: 'none',
            }}
          >
            <Typography
              component='div'
              sx={{
                fontSize: isFullHour ? '0.8rem' : '0.65rem',
                fontWeight: isFullHour ? 700 : 400,
                lineHeight: 1,
                color: isFullHour ? 'text.primary' : 'text.secondary',
              }}
            >
              {isFullHour ? String(slot.time).split(':')[0] : minutePart}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    );
  });

  if (isGridMode) {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          ml: `${startOffsetPx}px`,
        }}
      >
        {onToggleExpand && (
          <Tooltip title='Kompakte Ansicht' arrow placement='left'>
            <IconButton
              size='small'
              onClick={onToggleExpand}
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                zIndex: 10,
                padding: '2px',
                color: 'text.secondary',
                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
              }}
            >
              <ExpandLessIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        )}
        {bars}
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        overflowX: 'auto',
        position: 'relative',
        scrollbarWidth: 'thin',
      }}
    >
      {onToggleExpand && (
        <Tooltip title='Kompakte Ansicht' arrow placement='left'>
          <IconButton
            size='small'
            onClick={onToggleExpand}
            sx={{
              position: 'absolute',
              right: 4,
              top: 4,
              zIndex: 4,
              padding: '2px',
              flexShrink: 0,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
            }}
          >
            <ExpandLessIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ minWidth: Math.max(850, bars.length * 22) }}>
        {(data.entertainmentName || data.capacity != null) && (
          <Box
            sx={{
              position: 'sticky',
              left: 0,
              zIndex: 3,
              display: 'flex',
              alignItems: 'center',
              px: 1,
              pt: 0.75,
              pb: 0.25,
              pr: onToggleExpand ? 4 : 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              {data.entertainmentName && (
                <Typography variant='subtitle2' sx={{ fontWeight: 700, lineHeight: 1 }}>
                  {data.entertainmentName}
                </Typography>
              )}
              {data.capacity != null && (
                <Typography variant='caption' sx={{ opacity: 0.6, lineHeight: 1 }}>
                  Slotkapazität: {data.capacity}
                </Typography>
              )}
              {data.totalBookings != null && (
                <Typography variant='caption' sx={{ opacity: 0.6, lineHeight: 1 }}>
                  · insgesamt {data.totalBookings} Buchung{data.totalBookings !== 1 ? 'en' : ''}
                </Typography>
              )}
              {data.ageSplit && (
                <Typography variant='caption' sx={{ opacity: 0.55, lineHeight: 1 }}>
                  · Kinder-Kapazität wird schraffiert dargestellt
                </Typography>
              )}
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', px: 1, pt: 0.5, pb: 0 }}>
          {bars}
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Public component ─────────────────────────────────────────────────────────

const EntertainmentCapacity = ({
  data,
  condensedMode = false,
  showAbsoluteCountInHeader = false,
  showTotalColumn = false,
  onToggleExpand,
  slotWidthPx,
  startOffsetPx,
  currentDate,
  autoScrollToCurrentTime = false,
  highlightCurrentSlot = false,
}) => {
  if (condensedMode) {
    return (
      <CondensedTableView
        data={data}
        showAbsoluteCountInHeader={showAbsoluteCountInHeader}
        showTotalColumn={showTotalColumn}
        onToggleExpand={onToggleExpand}
        currentDate={currentDate}
        autoScrollToCurrentTime={autoScrollToCurrentTime}
        highlightCurrentSlot={highlightCurrentSlot}
      />
    );
  }

  return (
    <BarChartView
      data={data}
      showAbsoluteCountInHeader={showAbsoluteCountInHeader}
      showTotalColumn={showTotalColumn}
      onToggleExpand={onToggleExpand}
      slotWidthPx={slotWidthPx}
      startOffsetPx={startOffsetPx}
    />
  );
};

export default EntertainmentCapacity;
